import {
  IKaseMasterName,
  IMasterNamePerson,
  IOffenderVictim,
  IOffenseDisabledMedhod,
  IOffenseDynamicOptions,
  IOffenseOption,
  IOffenseOptionItem,
  IPopulateRuleItem,
  IValidateRuleItem,
  MasterNameType,
  OffenseGenericMethod,
  RuleValuesItemsMethod,
} from '@/models';

/**
 * Predicts the sequence name (e.g. "Unknown Person-3") that the backend will
 * assign when the pending unknown offender is eventually created.
 *
 * Mirrors backend logic in common_be MasterNameService.generateUnknownNameAlias /
 * createNewUnknownPersonName.
 */
export function predictUnknownPersonName(
  kaseMasterNames: IKaseMasterName[],
  offenderVictims: IOffenderVictim[],
  currentOffenderVictim?: IOffenderVictim,
): string {
  const existingNumbers = kaseMasterNames
    .filter(
      ({ masterName }) =>
        masterName?.type === MasterNameType.PERSON &&
        (masterName as IMasterNamePerson)?.isUnknown,
    )
    .map(({ masterName }) => {
      const firstName = (masterName as IMasterNamePerson).firstName ?? '';
      const parts = firstName.split('-');
      return parts.length > 1 ? Number(parts[1]) || 0 : 0;
    });

  const maxExisting =
    existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;

  const pendingUnknowns = offenderVictims.filter(
    (ov) => (ov.offender as IMasterNamePerson)?.isUnknown && !ov.offender._id,
  );

  if (
    currentOffenderVictim &&
    (currentOffenderVictim.offender as IMasterNamePerson)?.isUnknown &&
    !currentOffenderVictim.offender._id
  ) {
    const index = pendingUnknowns.findIndex(
      (ov) => ov.offender === currentOffenderVictim.offender,
    );
    const pendingIndex = index >= 0 ? index : pendingUnknowns.length - 1;
    return `Unknown Person-${maxExisting + pendingIndex + 1}`;
  }

  return `Unknown Person-${maxExisting + pendingUnknowns.length + 1}`;
}

export const getDisabledRules = (disable_rules: string[]) => {
  return disable_rules?.map((expression) => {
    // must be here, implicit eval is dissalowed
    // eslint-disable-next-line @typescript-eslint/no-implied-eval
    return new Function(
      'formValues',
      `return ${expression}`,
    ) as IOffenseDisabledMedhod;
  });
};

const getOptionsIds = <T>(
  populate_rules: Array<IPopulateRuleItem>,
  items: Map<string, IOffenseOptionItem>,
  formValues?: T,
) => {
  let optionIds: string[] = [];
  if (populate_rules && !!populate_rules.length) {
    for (const rule of populate_rules) {
      const expression = rule.condition;
      // must be here, implicit eval is dissalowed
      // eslint-disable-next-line @typescript-eslint/no-implied-eval
      const validateTest = new Function('formValues', `return ${expression}`);
      try {
        if (formValues && validateTest(formValues)) {
          optionIds = [...optionIds, ...rule.values];
        }
      } catch {
        // expression may reference undefined form values
      }
    }
  } else {
    optionIds = items ? [...items.keys()] : [];
  }

  return optionIds;
};

export const getPopulateItems = <T>(
  populate_rules: IPopulateRuleItem[],
  currentOptions: IOffenseOption,
  formValues?: T,
) => {
  let currentCategory = '';

  return (
    getOptionsIds<T>(populate_rules, currentOptions?.items, formValues).reduce(
      (acc, value) => {
        if (
          currentOptions.items.get(value)?.category &&
          currentCategory !== currentOptions.items.get(value)?.category
        ) {
          currentCategory =
            currentOptions.items.get(value)?.category || 'Unnamed category';
          acc.push({
            group: currentCategory,
            label: currentCategory,
            value: '',
          });
        }
        acc.push({
          label: `${currentOptions.items.get(value)?.label}`,
          value,
        });
        return acc;
      },
      [] as {
        group?: string;
        label: string;
        value: any;
      }[],
    ) || []
  );
};

export const getRules = (itemRules: IPopulateRuleItem[]) => {
  return itemRules?.map((validateRule) => {
    const expression = validateRule.condition;
    // must be here, implicit eval is dissalowed
    // eslint-disable-next-line @typescript-eslint/no-implied-eval
    return new Function(
      'formValues',
      `if(${expression}) {return [${validateRule.values}] }`,
    ) as RuleValuesItemsMethod;
  });
};

export const getValidateRules = (validate_rules: IValidateRuleItem[]) => {
  return validate_rules?.map((validateRule) => {
    const expression = validateRule.condition;
    // must be here, implicit eval is dissalowed
    // eslint-disable-next-line @typescript-eslint/no-implied-eval
    return new Function(
      'currentValue',
      'formValues',
      `if(${expression}) return ${validateRule.validateReturn}`,
    ) as OffenseGenericMethod;
  });
};

export const extractDefaultValueRules = <K>(
  rule: IPopulateRuleItem,
  formValues: K,
) => {
  const expression = rule.condition;
  // eslint-disable-next-line @typescript-eslint/no-implied-eval
  const validateTest = new Function('formValues', `return ${expression}`);
  return validateTest(formValues) ? (rule.values as any) : null;
};

export const getDropdownItems = (
  type: string,
  allOffenseOptions: IOffenseOption[],
) => {
  if (!allOffenseOptions) return [];
  const currentOptionItem = allOffenseOptions?.find(
    (typeOptions) => typeOptions.type === type,
  );

  const theItems = currentOptionItem
    ? Array.from(currentOptionItem.items.entries())
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
        .map(([key, { label }]) => {
          return {
            label,
            value: key,
          };
        })
    : [];

  return theItems;
};

export const getOffenseOptionItems = (
  typeOfOffens: string,
  allOffenseOptions: IOffenseOption[],
) => {
  const currentOptionItem = allOffenseOptions?.find(
    (typeOptions) => typeOptions.type === typeOfOffens,
  );
  return currentOptionItem ? currentOptionItem.items : [];
};

export const getCurrentDynamicOptions = (
  itemsKey: string,
  allOffenseOptions: IOffenseOption[],
) => {
  return allOffenseOptions.find((options) => options.type === itemsKey);
};

export const getItemDynamicOptions = <T>(
  itemsKey: string,
  allOffenseOptions: IOffenseOption[],
  allFormValues: T,
): IOffenseDynamicOptions<T> | null => {
  const currentOptions = getCurrentDynamicOptions(itemsKey, allOffenseOptions);
  if (!currentOptions) {
    return null;
  }

  const {
    populate_rules,
    disable_rules,
    validate_rules,
    title,
    fixed_items,
    submit_rules,
  } = currentOptions;

  const items = getPopulateItems(populate_rules, currentOptions, allFormValues);

  const validateMethods: Array<OffenseGenericMethod> =
    getValidateRules(validate_rules);

  const disabledRules: Array<IOffenseDisabledMedhod> =
    getDisabledRules(disable_rules);

  const fixedRules = fixed_items ? getRules(fixed_items) : [];

  const submitRules = submit_rules ? getRules(submit_rules) : [];

  return {
    label: title,
    items,
    ...(fixedRules && {
      fixedItems: <R>(formValues: R) => {
        let allFixedItems: Array<string> = [];
        for (const getFixedItems of fixedRules) {
          const fixedRule = getFixedItems(formValues) || [];
          allFixedItems = [...allFixedItems, ...fixedRule];
        }
        return allFixedItems;
      },
    }),
    ...(submitRules && {
      submitRules: <R>(formValues: R) => {
        let allSubmitItems: Array<string> = [];
        for (const getSubmitItems of submitRules) {
          const submitRule = getSubmitItems(formValues) || [];
          allSubmitItems = [...allSubmitItems, ...submitRule];
        }
        return allSubmitItems;
      },
    }),
    setDisable: (formValues) => {
      for (const rule of disabledRules) {
        const result = rule(formValues);
        if (result) return result;
      }
      return false;
    },
    ...(validateMethods && {
      rules: {
        validate: <S>(currentValue: any, formValues: S) => {
          for (const rule of validateMethods) {
            const result = rule(currentValue, formValues);
            if (result) return result;
          }
        },
      },
    }),
  };
};

export const handleDefaultValueRules = <T>(
  allOffenseOptions: IOffenseOption[],
  setValue: any,
  formValues: T,
  callbacks?: Record<string, () => any>,
): void => {
  for (const currentOption of allOffenseOptions) {
    const defaultValueRules = currentOption?.default_value_rules;
    if (!defaultValueRules || !defaultValueRules.length) continue;
    let optionValues: string[] = [];
    for (const rule of defaultValueRules) {
      let validateVal = extractDefaultValueRules<T>(rule, formValues);
      if (validateVal === null) {
        continue;
      } else if (
        callbacks &&
        typeof validateVal === 'string' &&
        typeof callbacks[validateVal] === 'function'
      ) {
        validateVal = callbacks[validateVal]() as string;
      }

      optionValues = [
        ...optionValues,
        ...(Array.isArray(validateVal) || typeof validateVal !== 'string'
          ? validateVal
          : [validateVal]),
      ];
    }
    if (optionValues && optionValues.length) {
      setValue(
        currentOption.type as any,
        currentOption?.multiple
          ? Array.isArray(optionValues)
            ? [...optionValues]
            : optionValues
          : optionValues.toString(),
      );
    }
  }
};

export const returnCleanValue = (value?: string) => {
  return value?.split('= ').pop();
};
